import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  ArrowUpRight,
  CalendarCheck,
  CalendarDays,
  Euro,
  Inbox,
  Users,
} from "lucide-react";
import { ErrorState, LoadingState, PageHeading, StatusBadge } from "@/components/app/AppUI";
import {
  campanasRepository,
  clientRepository,
  dashboardRepository,
  eventRepository,
  leadRepository,
  paymentRepository,
  quoteRepository,
} from "@/data/repositories";
import { cobrosProximos, fechaDeLaBoda, filasClientes, nombreDeLaPareja } from "@/domain/bodas";
import { rangoPeriodo } from "@/domain/campanas";
import { textoImporte } from "@/domain/cobros";
import { formatMoney } from "@/domain/money";

export const Route = createFileRoute("/_authenticated/app/")({ component: Dashboard });

function Dashboard() {
  const query = useQuery({ queryKey: ["dashboard"], queryFn: () => dashboardRepository.summary() });
  if (query.isLoading) return <LoadingState label="Preparando el backstage" />;
  if (query.error || !query.data)
    return <ErrorState message={query.error?.message ?? "No se pudo cargar el dashboard"} />;
  const data = query.data;

  return (
    <div className="space-y-8">
      <PageHeading
        eyebrow="Backstage · Hoy"
        title="Dashboard"
        description="Lo importante primero: fechas, preparación y dinero pendiente."
        action={
          <Link to="/app/leads" className="btn-primary">
            Ver leads <ArrowUpRight className="size-4" />
          </Link>
        }
      />

      <TarjetaHoy />

      <section className="grid gap-px border border-border bg-border sm:grid-cols-2 xl:grid-cols-5">
        <Stat label="Bodas activas" value={data.events} icon={CalendarDays} to="/app/clientes" />
        <Stat label="Leads nuevos" value={data.contacts} icon={Inbox} to="/app/leads" accent />
        <Stat label="Clientes activos" value={data.clients} icon={Users} to="/app/clientes" />
        <Stat
          label="Ingresos acordados"
          value={formatMoney(data.agreed)}
          icon={Euro}
          to="/app/clientes"
        />
        <Stat
          label="Pendiente de cobro"
          value={formatMoney(data.pending)}
          icon={Euro}
          to="/app/clientes"
          accent
        />
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <TarjetaCobrosProximos />
        <TarjetaCampanas />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="panel">
          <div className="panel-header">
            <div>
              <p className="eyebrow">Agenda</p>
              <h2 className="mt-1 font-display text-xl">Próximos eventos</h2>
            </div>
            <Link to="/app/calendario" className="eyebrow hover:text-primary">
              Calendario →
            </Link>
          </div>
          {data.upcoming.length === 0 ? (
            <p className="p-6 text-muted-foreground">No hay eventos próximos.</p>
          ) : (
            data.upcoming.map((event) => (
              <Link
                key={event.id}
                to="/app/eventos/$id"
                params={{ id: event.id }}
                className="data-row grid-cols-[4rem_1fr_auto]"
              >
                <time className="font-mono text-xs text-primary">
                  {event.starts_at
                    ? new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short" }).format(
                        new Date(event.starts_at),
                      )
                    : "S/F"}
                </time>
                <div>
                  <p className="font-semibold">{event.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {event.venue ?? "Lugar pendiente"}
                  </p>
                </div>
                <StatusBadge status={event.status} />
              </Link>
            ))
          )}
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <p className="eyebrow">Prioridad</p>
              <h2 className="mt-1 font-display text-xl">Tareas próximas</h2>
            </div>
            <Link to="/app/tareas" className="eyebrow hover:text-primary">
              Ver todas →
            </Link>
          </div>
          {data.tasks.length === 0 ? (
            <p className="p-6 text-muted-foreground">Todo al día.</p>
          ) : (
            data.tasks.map((task) => (
              <div key={task.id} className="data-row grid-cols-[1rem_1fr_auto]">
                <span
                  className={`size-2 rounded-full ${task.priority === 1 ? "bg-primary" : "bg-foreground/25"}`}
                />
                <div>
                  <p>{task.title}</p>
                  <p className="text-sm text-muted-foreground">{task.events?.title ?? "General"}</p>
                </div>
                <time className="text-sm text-muted-foreground">
                  {task.due_at
                    ? new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short" }).format(
                        new Date(task.due_at),
                      )
                    : "—"}
                </time>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

/** Acceso directo a la agenda de ventas del día. */
function TarjetaHoy() {
  const agenda = useQuery({ queryKey: ["agenda"], queryFn: () => leadRepository.agenda() });
  const pendientes = agenda.data?.length ?? 0;
  return (
    <Link
      to="/app/hoy"
      className="group flex items-center gap-4 border border-border bg-white p-5 transition-colors hover:bg-secondary"
    >
      <CalendarCheck className="size-5 text-primary" />
      <p className="flex-1">
        <span className="font-display text-2xl tracking-[-0.04em]">Hoy</span>
        <span className="ml-3 text-muted-foreground">
          {agenda.isLoading
            ? "Calculando…"
            : pendientes === 1
              ? "1 llamada pendiente"
              : `${pendientes} llamadas pendientes`}
        </span>
      </p>
      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

/** Bodas de los próximos 30 días con dinero pendiente. */
function TarjetaCobrosProximos() {
  const clientes = useQuery({ queryKey: ["clients"], queryFn: () => clientRepository.list() });
  const bodas = useQuery({ queryKey: ["events"], queryFn: () => eventRepository.list() });
  const presupuestos = useQuery({ queryKey: ["quotes"], queryFn: () => quoteRepository.list() });
  const pagos = useQuery({ queryKey: ["payments"], queryFn: () => paymentRepository.list() });
  const leads = useQuery({ queryKey: ["leads"], queryFn: () => leadRepository.list() });
  const consultas = [clientes, bodas, presupuestos, pagos, leads];
  const cargando = consultas.some((c) => c.isLoading);
  const error = consultas.find((c) => c.error)?.error;

  const ahora = new Date();
  const nombresLead = new Map(
    (leads.data ?? [])
      .filter((lead) => lead.converted_client_id)
      .map((lead) => [lead.converted_client_id as string, lead.name]),
  );
  const filas = cobrosProximos(
    filasClientes(
      clientes.data ?? [],
      bodas.data ?? [],
      presupuestos.data ?? [],
      pagos.data ?? [],
      ahora,
    ),
    ahora,
  );

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Dinero</p>
          <h2 className="mt-1 font-display text-xl">Cobros próximos</h2>
        </div>
        <Link to="/app/clientes" className="eyebrow hover:text-primary">
          Clientes →
        </Link>
      </div>
      {cargando ? (
        <p className="p-6 text-muted-foreground">Calculando…</p>
      ) : error ? (
        <p className="p-6 text-red-700">{error.message}</p>
      ) : filas.length === 0 ? (
        <p className="p-6 text-muted-foreground">
          Ninguna boda de los próximos 30 días tiene pagos pendientes.
        </p>
      ) : (
        filas.map((fila) => (
          <Link
            key={fila.cliente.id}
            to="/app/clientes/$id"
            params={{ id: fila.cliente.id }}
            className="data-row grid-cols-[1fr_auto]"
          >
            <div>
              <p className="font-semibold">
                {nombreDeLaPareja(fila.cliente, nombresLead.get(fila.cliente.id))}
              </p>
              <p className="text-sm text-muted-foreground">
                {fechaDeLaBoda(fila.boda?.starts_at ?? null)}
              </p>
            </div>
            <p className="text-right">
              <span className="block font-semibold">{textoImporte(fila.cobros.pendiente)}</span>
              <span className="text-xs text-muted-foreground">pendiente</span>
            </p>
          </Link>
        ))
      )}
    </section>
  );
}

/** Gasto de Meta de los últimos 30 días y lo que ha dado en bodas. */
function TarjetaCampanas() {
  const rango = rangoPeriodo("30d", new Date());
  const campanas = useQuery({
    queryKey: ["campanas", rango.desde, rango.hasta],
    queryFn: () => campanasRepository.resumen(rango),
  });
  const total = campanas.data?.find((fila) => fila.nivel === "total") ?? null;
  const cifras = total
    ? [
        { etiqueta: "Gasto", valor: textoImporte(total.gasto_cents) },
        { etiqueta: "Leads", valor: String(total.leads_meta) },
        { etiqueta: "Reservas", valor: String(total.reservados) },
        {
          etiqueta: "Coste por boda",
          valor: total.coste_boda_cents === null ? "–" : textoImporte(total.coste_boda_cents),
        },
      ]
    : [];

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Meta</p>
          <h2 className="mt-1 font-display text-xl">Campañas · últimos 30 días</h2>
        </div>
        <Link to="/app/campanas" className="eyebrow hover:text-primary">
          Campañas →
        </Link>
      </div>
      {campanas.isLoading ? (
        <p className="p-6 text-muted-foreground">Calculando…</p>
      ) : campanas.error ? (
        <p className="p-6 text-red-700">{campanas.error.message}</p>
      ) : !total ? (
        <p className="p-6 text-muted-foreground">
          Sin datos todavía: aparecen cuando corre la sincronización con Meta.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-px bg-border">
          {cifras.map((cifra) => (
            <div key={cifra.etiqueta} className="bg-white px-5 py-4">
              <p className="eyebrow">{cifra.etiqueta}</p>
              <p className="mt-1 font-display text-3xl tracking-[-0.04em]">{cifra.valor}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  to,
  accent = false,
}: {
  label: string;
  value: number | string;
  icon: typeof CalendarDays;
  to: string;
  accent?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`group relative min-h-40 bg-white p-5 transition-colors hover:bg-secondary ${accent ? "after:absolute after:inset-x-0 after:top-0 after:h-1 after:bg-primary" : ""}`}
    >
      <Icon className={`size-5 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      <p className="mt-8 font-display text-4xl tracking-[-0.06em]">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </Link>
  );
}
